import { isEmpty, isNotEmpty } from '@terros/common'

export type SearchResultType = 'Account' | 'Location'

export type RankableSearchResult = {
  type: SearchResultType
  primaryText: string
  secondaryText?: string
}

type RankSearchResultsParams<T extends RankableSearchResult> = {
  results: T[]
  query: string
}

type ScoredResult<T extends RankableSearchResult> = {
  result: T
  score: number
}

type ScoreFieldParams = {
  field: string
  query: string
  queryTokens: string[]
  weight: number
}

type ScoreTokenParams = {
  field: string
  fieldTokens: string[]
  token: string
}

const PRIMARY_TEXT_WEIGHT = 1
const SECONDARY_TEXT_WEIGHT = 0.5
const TOKEN_SEPARATOR = /[\s\p{P}]+/u
const WORD_CHARACTER = /[\p{L}\p{N}]/u

export function rankSearchResults<T extends RankableSearchResult>({ results, query }: RankSearchResultsParams<T>): T[] {
  const normalizedQuery = normalizeForSearch(query)
  if (isEmpty(normalizedQuery)) return results
  const queryTokens = toSearchTokens(normalizedQuery)
  return results
    .map((result) => ({ result, score: scoreResult({ result, query: normalizedQuery, queryTokens }) }))
    .sort(compareScoredResults)
    .map(({ result }) => result)
}

function scoreResult({
  result,
  query,
  queryTokens,
}: {
  result: RankableSearchResult
  query: string
  queryTokens: string[]
}): number {
  const primaryScore = scoreField({
    field: normalizeForSearch(result.primaryText),
    query,
    queryTokens,
    weight: PRIMARY_TEXT_WEIGHT,
  })
  const secondaryScore = scoreField({
    field: normalizeForSearch(result.secondaryText ?? ''),
    query,
    queryTokens,
    weight: SECONDARY_TEXT_WEIGHT,
  })
  return primaryScore + secondaryScore
}

function scoreField({ field, query, queryTokens, weight }: ScoreFieldParams): number {
  if (isEmpty(field)) return 0
  const fieldTokens = toSearchTokens(field)
  const tokenScore = queryTokens.reduce((total, token) => total + scoreToken({ field, fieldTokens, token }), 0)
  return Math.trunc((scorePhrase(field, query) + tokenScore) * weight)
}

function scorePhrase(field: string, query: string): number {
  if (field === query) return 100
  if (field.startsWith(query)) return 60
  if (containsAtWordBoundary(field, query)) return 40
  if (field.includes(query)) return 20
  return 0
}

function scoreToken({ field, fieldTokens, token }: ScoreTokenParams): number {
  if (fieldTokens.includes(token)) return 10
  if (fieldTokens.some((fieldToken) => fieldToken.startsWith(token))) return 5
  if (field.includes(token)) return 2
  return 0
}

function containsAtWordBoundary(field: string, query: string): boolean {
  let index = field.indexOf(query)
  while (index !== -1) {
    if (isWordBoundary(field, index)) return true
    index = field.indexOf(query, index + query.length)
  }
  return false
}

function isWordBoundary(field: string, index: number): boolean {
  if (index === 0) return true
  return !WORD_CHARACTER.test(field.charAt(index - 1))
}

function compareScoredResults<T extends RankableSearchResult>(a: ScoredResult<T>, b: ScoredResult<T>): number {
  if (a.score !== b.score) return b.score - a.score
  const typeOrder = getTypePriority(a.result.type) - getTypePriority(b.result.type)
  if (typeOrder !== 0) return typeOrder
  return a.result.primaryText.localeCompare(b.result.primaryText, undefined, { sensitivity: 'base' })
}

function getTypePriority(type: SearchResultType): number {
  if (type === 'Account') return 0
  return 1
}

function normalizeForSearch(value: string): string {
  return value.trim().toLowerCase()
}

function toSearchTokens(value: string): string[] {
  return value.split(TOKEN_SEPARATOR).filter(isNotEmpty)
}
